import {faUpload} from '@fortawesome/pro-regular-svg-icons';
import {faImages} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button} from 'antd';
import {RcFile} from 'antd/lib/upload';
import {ReactNode, useCallback, useEffect, useState} from 'react';

import {LangKeyType} from '../../provider/locale/translation/type';
import {CustomFileType, UploadFileResponseType} from '../../service/file-upload/file-upload-type';
import {useFormRules} from '../../service/form/form-rules-hook';
import {FileFormInput} from '../form/file-upload-item/file-form-input';
// Reuse the stories uploader styling so the media form is visually identical across features.
import * as styles from '../stories/yandex-story-form/slides-content-steps/content-step/upload-images-step/yandex-story-upload-images-step.scss';
import {Text} from '../text/text';

import {PublicationWizardStep} from './publication-wizard-step';

type PublicationMediaImageType = {id: number; url: string};

// Default upload size cap (MB); each publication type may override via props.
const PUBLICATION_MEDIA_DEFAULT_MAX_FILE_SIZE = 10;

type FilesMapType = Record<string, CustomFileType & UploadFileResponseType>;

type PropsType = {
    value?: Array<PublicationMediaImageType>;
    onChange?: (images: Array<PublicationMediaImageType>) => void;
    upload: (file: RcFile) => Promise<{id: number}>;
    remove?: (imageId: number) => void;
    maxCount: number;
    // Allowed image dimensions (px). Differ per publication type per Yandex help.
    minWidth: number;
    minHeight: number;
    maxWidth: number;
    maxHeight: number;
    maxFileSize?: number;
    banner: ReactNode;
    uploaderTextLangKey: LangKeyType;
    uploaderHintLangKey?: LangKeyType;
    onPreviousStep: VoidFunction;
    onStepFinished: VoidFunction;
    onStepActivated: (width: number) => void;
    width: number;
};

function buildInitialFilesMap(images: Array<PublicationMediaImageType>): FilesMapType {
    return Object.fromEntries(
        images.map((image) => [
            String(image.id),
            {
                uid: String(image.id),
                id: String(image.id),
                name: `image-${image.id}`,
                url: image.url,
                status: 'done' as const,
            },
        ])
    );
}

// Shared media-upload wizard step (same uploader form as stories), used as a Form.Item control.
export function PublicationMediaStep(props: PropsType): JSX.Element {
    const {
        value,
        onChange,
        upload,
        remove,
        maxCount,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        maxFileSize = PUBLICATION_MEDIA_DEFAULT_MAX_FILE_SIZE,
        banner,
        uploaderTextLangKey,
        uploaderHintLangKey,
        onPreviousStep,
        onStepFinished,
        onStepActivated,
        width,
    } = props;

    const {validateImageDimension} = useFormRules();

    const [filesMap, setFilesMap] = useState<FilesMapType>(() => buildInitialFilesMap(value ?? []));

    useEffect(() => {
        onStepActivated(width);
    }, [onStepActivated, width]);

    useEffect(() => {
        const entries = Object.values(filesMap);

        if (entries.some((file) => file.status === 'uploading')) {
            return;
        }

        onChange?.(
            entries.filter((file) => file.status === 'done').map((file) => ({id: Number(file.id), url: file.url}))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesMap]);

    const uploadWrapper = useCallback(
        async (file: RcFile): Promise<UploadFileResponseType> => {
            const {id} = await upload(file);

            return {id: String(id), url: URL.createObjectURL(file), name: file.name};
        },
        [upload]
    );

    const handleRemove = useCallback(
        (fileUid: string) => {
            const imageId = Number(fileUid);

            if (!Number.isNaN(imageId)) {
                remove?.(imageId);
            }
        },
        [remove]
    );

    const fileList = Object.values(filesMap);
    const isLimitReached = fileList.length >= maxCount;

    // Can't proceed to the final form until there is at least one image and every image has finished
    // uploading without an error.
    const isNextDisabled =
        fileList.length === 0 || fileList.some((file) => file.status === 'uploading' || file.status === 'error');

    return (
        <PublicationWizardStep
            leftFooterButtonConfig={{langKeyType: 'BUTTON__BACK', onClick: onPreviousStep}}
            rightFooterButtonConfig={{disabled: isNextDisabled, langKeyType: 'BUTTON__NEXT', onClick: onStepFinished}}
        >
            <div className={styles.YandexStoryUploadImagesStep}>
                <div className={styles.YandexStoryUploadImagesStep_description}>
                    <div>{banner}</div>
                    <FontAwesomeIcon className={styles.YandexStoryUploadImagesStep_descriptionIcon} icon={faImages} />
                </div>

                <FileFormInput
                    onChange={setFilesMap}
                    remove={handleRemove}
                    upload={uploadWrapper}
                    uploadProps={{accept: '.png,.jpg', maxCount, maxFileSize}}
                    validationRules={[
                        async (file: RcFile) =>
                            validateImageDimension(file, {
                                min: {height: minHeight, width: minWidth},
                                max: {height: maxHeight, width: maxWidth},
                            }),
                    ]}
                    value={filesMap}
                >
                    {!isLimitReached && (
                        <Button className={styles.YandexStoryUploadImagesStep_uploadArea}>
                            <FontAwesomeIcon
                                className={styles.YandexStoryUploadImagesStep_uploadAreaIcon}
                                icon={faUpload}
                            />
                            <Text block large stringKey={uploaderTextLangKey} />
                            {uploaderHintLangKey && <Text block secondary stringKey={uploaderHintLangKey} />}
                        </Button>
                    )}
                </FileFormInput>
            </div>
        </PublicationWizardStep>
    );
}
